import {HttpBackend, HttpClient, HttpHeaders} from "@angular/common/http";
import {Injectable, Injector} from "@angular/core";
import {catchError, map} from "rxjs/operators";
import OktaAuth from "@okta/okta-auth-js";
import {of} from "rxjs";
import {OKTA_AUTH, OktaAuthConfigService} from "@okta/okta-angular";
import {CustomStorageManager} from "../common/custom-storage-manager";
import {environment} from "../../environments/environment";

@Injectable()
export class AppConfigService {

	constructor(
		private httpBackend: HttpBackend,
		private injector: Injector
	) {}

	private get oktaAuth(): OktaAuth {
		return this.injector.get(OKTA_AUTH);
	}

	async getAccessToken(): Promise<string | undefined> {
		return this.oktaAuth.getAccessToken();
	}

	async renewAccessToken(): Promise<string | undefined> {
		try {
			const newToken = await this.oktaAuth.token.renewTokens({
				scopes: ['openid', 'profile', 'email']
			});
			return newToken.accessToken?.accessToken;
		} catch (error) {
			return `Error renewing access token: ${error}`;
		}
	}

	async isAccessTokenExpired(): Promise<boolean> {
		const accessToken = await this.oktaAuth.tokenManager.get('accessToken');
		const expiresAt = accessToken?.expiresAt ?? 0;
		return expiresAt < Math.floor(Date.now() / 1000);
	}

	loadAuthConfig() {
		const configService = this.injector.get(OktaAuthConfigService);
		const cookiesSecure = typeof window !== "undefined" && window.location?.protocol === "https:";
		return new HttpClient(this.httpBackend)
			.get("/assets/config.json", {
				headers: new HttpHeaders({ "Cache-Control": "no-cache", "Pragma": "no-cache", "Expires": "0" })
			}).pipe(
				map((authConfig: any) => {
					const customStorage = new CustomStorageManager();
					const redirectUri = window.location.origin + "/callback";
					const oktaAuth = new OktaAuth({
						...authConfig,
						redirectUri,
						cookies: { ...authConfig.cookies, secure: cookiesSecure },
						storageManager: authConfig?.storageManager ? { ...authConfig.storageManager, cache: { storageProvider: customStorage } } : undefined
					});
					return configService.setConfig({ oktaAuth });
				}),
				catchError(() => {
					if (!environment.production) {
						const oidc = environment?.oidc;
						if (oidc?.issuer && oidc?.clientId) {
							const oktaAuth = new OktaAuth({
								issuer: oidc.issuer,
								clientId: oidc.clientId,
								redirectUri: window.location.origin + (oidc.redirectUri || "/callback"),
								scopes: oidc.scopes ?? ["openid", "profile", "email"],
								pkce: oidc.pkce !== false,
								cookies: { secure: typeof window !== "undefined" && window.location?.protocol === "https:" }
							});
							configService.setConfig({ oktaAuth });
						}
					}
					return of(undefined);
				})
			);
	}

	loadAppHealthCheckConfig() {
		return new HttpClient(this.httpBackend)
			.get('/assets/health-check.json', {
				headers: new HttpHeaders({
					"Cache-Control": "no-cache",
					"Pragma": "no-cache",
					"Expires": "0"
				})
			})
			.pipe(
				map(data => data),
				catchError(error => {
					console.error(error);
					return of(error);
				})
			);
	};

	loadAppDmpCheckConfig() {
		return new HttpClient(this.httpBackend)
			.get('/rest/dmp/component.json', {
				headers: new HttpHeaders({
					"Cache-Control": "no-cache",
					"Pragma": "no-cache",
					"Expires": "0"
				})
			}).pipe(
				map(data => data),
				catchError(error => {
					console.error(error);
					return of(error);
				})
			);
	};

	loadFutureForecastConfig() {
		return new HttpClient(this.httpBackend)
			.get('/assets/future-forecast-weightings.json', {
				headers: new HttpHeaders({
					"Cache-Control": "no-cache",
					"Pragma": "no-cache",
					"Expires": "0"
				})
			}).pipe(
				map(data => data),
				catchError(error => {
					console.error(error);
					return of(error);
				})
			);
	};
}
